import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { toCurrency, toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { Order, OrderLine } from "../../../controlKitchenTypes"
import { MonetaryAmount } from "../../../../currency/currencyTypes"
import { STATUS_MAP } from "../meetingService/constants"
import { FilteredStatuses, MeetingServiceOrderStatus } from "../meetingService/meetingServiceTypes"
import { OrderType } from "../../../../payments/orderTypes"

type useExportOrdersToCsvParams = Readonly<{
    data: Order[] | null | undefined
    startDate: DateTime
    endDate: DateTime
    showStatusColumn?: boolean
}>
export function useExportOrdersToCsv({
    data,
    startDate,
    endDate,
    showStatusColumn = false,
}: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()
    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Orders-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    function translateOrderStatus(status: FilteredStatuses | undefined): string {
        if (!status) return ""
        const statusInfo = STATUS_MAP[status] || STATUS_MAP[MeetingServiceOrderStatus.DEFAULT]
        return l10n.getString(statusInfo.stateTranslationId)
    }

    function getDeliveryTimeString(order: Order) {
        const deliveryTime = order.deliveries?.[0]?.deliveryTime ?? order.deliveryDetails?.time
        return dateTimeFactory.formatDateTimeSafe(dateTimeFactory.fromApiOrUndefined(deliveryTime)) ?? ""
    }

    const mapOrderToCsvRow = (order: Order, orderLine: OrderLine) => {
        const baseRow = {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(endDate),
            "Order Id": order.id,
            "PSP reference": order.paymentDetails?.paymentId ?? "",
            "Transaction time": order.created,
            "Delivery time": getDeliveryTimeString(order),
            "Transaction type": order.orderType === OrderType.REFUND ? "refund" : "payment",
            "Product id": orderLine.productId,
            "Product name": orderLine.name,
            Items: orderLine.items,
            [`Item price (${getCurrencyString(orderLine.itemPrice)})`]: getAmountString(orderLine.itemPrice),
            [`Amount (${getCurrencyString(orderLine.price)})`]: getAmountString(orderLine.price),
            "User id": order.customer?.id ?? "",
            "Employee id": order.customer?.employeeId ?? "",
            Initials: order.customer?.initials ?? "",
            Name: order.customer?.displayName ?? "",
            Email: order.customer?.email ?? "",
            "Phone number": order.customer?.phone ?? "",
            "Contact person": order.accountingCustomerParty?.contactPerson ?? "",
            "User group id": order.customer?.userGroup?.id ?? "",
            "User group": order.customer?.userGroup?.name ?? "",
            Company: order.accountingCustomerParty?.name ?? "",
            "Payment method": order.paymentDetails?.method
                ? l10n.getStringForEnum("purchases-payment-method", order.paymentDetails?.method ?? "", "")
                : "",
            "Sales location": "",
            Channel: order.salesChannel ?? "",
            "Delivery date": order?.deliveries?.[0]?.deliveryTime,
            Acquirer: order.paymentDetails.acquirer?.name,
        }

        if (showStatusColumn) {
            return {
                ...baseRow,
                "Order note": order.orderNote ?? "",
                "Order status": translateOrderStatus(order?.deliveryDetails?.statusDetails?.status),
            }
        }

        return baseRow
    }

    return useCallback(() => {
        const exportRows =
            data && data.length > 0
                ? data.flatMap((order) =>
                      order.orderLines ? order.orderLines.map((orderLine) => mapOrderToCsvRow(order, orderLine)) : [],
                  )
                : [
                      Object.keys(mapOrderToCsvRow({} as Order, {} as OrderLine)).reduce(
                          (acc, key) => ({ ...acc, [key]: "" }),
                          {},
                      ),
                  ]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n, showStatusColumn])
}
